/**
 * The model's reasoning as Cursor shows it: one quiet line, "Thinking" with a
 * shimmer while it streams, "Thought for 4s · <its first sentence>" once done,
 * the text folded under it in muted 12px prose. Opens itself while streaming
 * and folds a moment after. Tool plumbing (calls, ids, paths) never shows.
 */
import { ChevronRight } from "lucide-react";
import { memo, useMemo, type FC } from "react";

import {
  Reasoning,
  ReasoningContent,
  ReasoningTrigger,
  useReasoning
} from "@/components/ai-elements/reasoning";
import { Shimmer } from "@/components/ai-elements/shimmer";
import { cn } from "@/lib/utils";

import { cleanReasoning, reasoningSummary } from "./reasoningText";

/** "Thought for 4s"; "Thought" when the time is not known (history). */
export const thoughtLabel = (
  streaming: boolean,
  seconds: number | undefined
): string => {
  if (streaming) return "Thinking";
  if (seconds === undefined || seconds <= 0) return "Thought";
  return `Thought for ${seconds}s`;
};

const Trigger: FC<{ summary: string }> = ({ summary }) => {
  const { isStreaming, isOpen, duration } = useReasoning();
  const label = thoughtLabel(isStreaming, duration);
  return (
    <>
      <span className="min-w-0 flex-1 truncate">
        {isStreaming ? (
          <Shimmer as="span" className="text-base" duration={1.6}>
            {label}
          </Shimmer>
        ) : (
          label
        )}
        {!isStreaming && !isOpen && summary ? (
          <span className="text-fg-4" data-testid="reasoning-summary">
            {" "}
            · {summary}
          </span>
        ) : null}
      </span>
      <ChevronRight
        className={cn(
          "size-3 shrink-0 text-fg-4 opacity-0 transition-[transform,opacity] group-hover/reason:opacity-100 group-data-[state=open]/reason:opacity-100",
          isOpen && "rotate-90"
        )}
      />
    </>
  );
};

type Props = {
  text: string;
  isStreaming: boolean;
  className?: string;
};

export const ReasoningRow: FC<Props> = memo(
  ({ text, isStreaming, className }) => {
    const clean = useMemo(() => cleanReasoning(text), [text]);
    const summary = useMemo(
      () => (isStreaming ? "" : reasoningSummary(text)),
      [text, isStreaming]
    );
    if (!clean && !isStreaming) return null;
    return (
      <Reasoning
        isStreaming={isStreaming}
        className={cn("group/reason mb-0 min-w-0", className)}
        data-testid="reasoning-row"
      >
        <ReasoningTrigger className="-ml-1 h-6 w-[calc(100%+0.25rem)] gap-1.5 rounded-sm px-1 text-base text-fg-3 hover:bg-fill-1 hover:text-fg">
          <Trigger summary={summary} />
        </ReasoningTrigger>
        <ReasoningContent className="mt-0.5 pb-1 text-sm leading-relaxed [&_p]:my-1 [&_ul]:my-1 [&_ul]:list-disc [&_ul]:pl-4 [&_ol]:my-1 [&_ol]:list-decimal [&_ol]:pl-4">
          {clean}
        </ReasoningContent>
      </Reasoning>
    );
  }
);
ReasoningRow.displayName = "ReasoningRow";
